import { ImageIcon } from "lucide-react"
import { type ChangeEvent, useId, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { MissionCoverCropper } from "@/components/missions/mission-cover-cropper"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import { COVER_ASPECT_CLASS } from "@/lib/mission-cover"
import { cn } from "@/lib/utils"

/**
 * OKS-643 — Recommended minimum cover dimensions (px), matching the model's
 * `Mission::COVER_CARD_VARIANT`. OKS-697 retargeted the cover ratio from
 * 16:9 to 3:2, so the `:card` variant is now 1200×800 and the below-minimum
 * advisory follows it. A source below either bound gets silently UPSCALED by
 * `resize_to_fill` into a blurry card, so we warn (never block) when a picked
 * file is under this size. Exported so the component's Vitest can assert
 * against the same source of truth.
 */
export const MISSION_COVER_MIN_W = 1200
export const MISSION_COVER_MIN_H = 800

/**
 * OKS-697 — Accepted cover MIME types (mirrors the server's
 * `Mission::COVER_IMAGE_CONTENT_TYPES`). Drives BOTH the file input's
 * `accept` filter AND the crop-vs-bypass decision on pick: a non-image type
 * can't be framed, so it skips the cropper and defers to server validation.
 */
export const COVER_ACCEPT_TYPES = ["image/jpeg", "image/png", "image/webp"]

/**
 * OKS-447 — Mission cover image upload control inside the mission form
 * (`mission-form.tsx`). Replaces the OKS-386 BYO-URL string (which never
 * shipped a form input — covers were seed/console-only until now).
 *
 * Mirrors the shipped `RewardCoverImageField` idiom: a plain
 * `<input type="file">` inside the parent Inertia `<Form>` (Inertia
 * auto-detects the file and submits multipart, so the controller receives
 * an `UploadedFile` on `mission[cover_image]`) + an in-form
 * `mission[remove_cover_image]` hidden flag (no DELETE route). The cover
 * is small (≤5 MB) so a plain multipart upload is fine — no DirectUpload
 * progress needed (contrast the brief-video field, which streams).
 *
 * OKS-697 — every pick/replace now routes through `MissionCoverCropper`
 * (a 3:2 `react-easy-crop` step) BEFORE anything is staged. The OS-picked
 * original is cleared from the input on pick; only the cropped 3:2 Blob is
 * injected back (via `DataTransfer`) and submitted, so no uncropped image
 * ever reaches the server and the framed area is what every surface shows.
 * The crop widget is NOT a form (no RHF/zod) — the only form is the parent
 * Inertia `<Form>`.
 *
 * Preview precedence (UX Spec §2): cropped file (object URL) → current
 * resolved cover (`currentUrl`, unless removal is staged) → empty
 * dropzone with the image-icon fallback. Legacy-URL-only covers show an
 * "imported from a link" note (UX Spec §1 edge).
 *
 * Crew form rules: plain shadcn `Input`/`Label`/`Button`/`Dialog`; NO
 * shadcn Form / react-hook-form / zod. Server errors render from the
 * parent `<Form>` render-prop `errors.cover_image`.
 */
/**
 * OKS-495 — i18n KEY overrides so the same control can label itself for a
 * different surface (the gift-product photo reuses this control inside the
 * mission form). Every key is optional and defaults to the OKS-447 cover
 * keys, so existing callers (and their tests) are byte-for-byte unchanged.
 */
export interface MissionImageFieldLabels {
  label: string
  previewAlt: string
  upload: string
  replace: string
  clear: string
  remove: string
  removeUndo: string
  legacyNote: string
  hint: string
  dimensionsWarning: string
  removeConfirmTitle: string
  removeConfirmBody: string
  removeConfirmCancel: string
  removeConfirmCta: string
}

const COVER_IMAGE_LABELS: MissionImageFieldLabels = {
  label: "missions.media.cover_label",
  previewAlt: "missions.media.cover_preview_alt",
  upload: "missions.media.cover_upload",
  replace: "missions.media.cover_replace",
  clear: "missions.media.cover_clear",
  remove: "missions.media.cover_remove",
  removeUndo: "missions.media.cover_remove_undo",
  legacyNote: "missions.media.cover_legacy_note",
  hint: "missions.media.cover_hint",
  dimensionsWarning: "missions.media.cover_dimensions_warning",
  removeConfirmTitle: "missions.media.cover_remove_confirm_title",
  removeConfirmBody: "missions.media.cover_remove_confirm_body",
  removeConfirmCancel: "missions.media.cover_remove_confirm_cancel",
  removeConfirmCta: "missions.media.cover_remove_confirm_cta",
}

export interface MissionCoverImageFieldProps {
  /** Resolved current cover URL from `mission.cover_image_display_url`
   *  (attached `:card` variant or legacy string). Null when none. */
  currentUrl: string | null
  /** True when an Active Storage attachment exists (gates Remove;
   *  legacy-URL-only rows can't be purged). From `mission.has_cover_image`. */
  hasCoverImage: boolean
  /** True when the cover is a legacy pasted-URL only — shows the note.
   *  From `mission.cover_image_is_legacy`. */
  isLegacy: boolean
  /** Validation error from the parent `<Form>` render-prop. */
  error?: string | string[]
  inputName?: string
  removeName?: string
  testId?: string
  /** OKS-495 — i18n key overrides; defaults to the cover-image keys. */
  labels?: Partial<MissionImageFieldLabels>
  /**
   * OKS-643 — Recommended minimum source dimensions `[width, height]` (px)
   * for the below-minimum advisory. Defaults to the cover `:card` size
   * (1200×800). Pass `null` to DISABLE the dimension probe entirely — e.g.
   * the gift-product photo reuse, which uses the original Active Storage
   * blob (no `:card` variant) and has no card-sharpness recommendation, so
   * the cover-specific "blurry on mission cards" advisory would be wrong.
   */
  minDimensions?: [number, number] | null
}

export function MissionCoverImageField({
  currentUrl,
  hasCoverImage,
  isLegacy,
  error,
  inputName = "mission[cover_image]",
  removeName = "mission[remove_cover_image]",
  testId = "mission-cover-image-field",
  labels,
  minDimensions = [MISSION_COVER_MIN_W, MISSION_COVER_MIN_H],
}: MissionCoverImageFieldProps) {
  const copy: MissionImageFieldLabels = { ...COVER_IMAGE_LABELS, ...labels }
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const inputId = useId()
  const helperId = `${inputId}-helper`
  const errorId = `${inputId}-error`
  const warningId = `${inputId}-dimensions-warning`

  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [removeStaged, setRemoveStaged] = useState(false)
  const [removeOpen, setRemoveOpen] = useState(false)
  // OKS-643 — non-blocking below-minimum dimension advisory (client-only).
  const [dimensionWarning, setDimensionWarning] = useState(false)
  // OKS-697 — crop step. A picked/replaced file opens the cropper; only the
  // cropped (3:2) Blob is ever staged into the hidden multipart input, so the
  // uncropped original is never submitted. `cropSrc` is the object URL of the
  // source image handed to `react-easy-crop`; `cropMime` passes the source
  // type through to the canvas export.
  const [cropOpen, setCropOpen] = useState(false)
  const [cropSrc, setCropSrc] = useState<string | null>(null)
  const [cropMime, setCropMime] = useState<string | undefined>(undefined)
  // OKS-452 — Edit-load skeleton. Gates ONLY the server-provided `currentUrl`
  // branch (UX Spec §2): a freshly picked/cropped local preview is an
  // in-memory object URL with no meaningful network wait, so it's never
  // skeleton-gated. Resets whenever `currentUrl` itself changes (e.g. a
  // different mission's edit form) so a stale "loaded" flag can't hide a
  // fresh skeleton opportunity — adjusted during render (React's documented
  // "resetting state when a prop changes" pattern) rather than in an effect,
  // avoiding a cascading-render setState-in-effect lint violation.
  const [currentUrlLoaded, setCurrentUrlLoaded] = useState(false)
  const [trackedCurrentUrl, setTrackedCurrentUrl] = useState(currentUrl)
  if (currentUrl !== trackedCurrentUrl) {
    setTrackedCurrentUrl(currentUrl)
    setCurrentUrlLoaded(false)
  }

  const hasError = Boolean(error)

  // OKS-643 — Decode the picked file off-screen to read its intrinsic size,
  // mirroring the badge/blazon uploader's `naturalWidth`/`naturalHeight`
  // probe. Sets an advisory (never a blocking error) when the source is
  // below `MISSION_COVER_MIN_W`×`MISSION_COVER_MIN_H` — `resize_to_fill`
  // would otherwise upscale it into a blurry card. Divergence from the
  // blazon idiom: on decode error we CLEAR the warning (an undecodable file
  // is the content-type/byte-size validators' job, not the size advisory).
  const probeDimensions = (file: File) => {
    // `null` disables the advisory entirely (e.g. gift-product photos).
    if (!minDimensions) return
    const [minW, minH] = minDimensions
    const url = URL.createObjectURL(file)
    const probe = new window.Image()
    probe.onload = () => {
      URL.revokeObjectURL(url)
      setDimensionWarning(
        probe.naturalWidth < minW || probe.naturalHeight < minH,
      )
    }
    probe.onerror = () => {
      URL.revokeObjectURL(url)
      setDimensionWarning(false)
    }
    probe.src = url
  }

  const onPick = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    setRemoveStaged(false)
    // Advisory probes the source dimensions (pre-crop) — unchanged UX.
    probeDimensions(file)

    // OKS-697 — a NON-image type can't be framed by the cropper (nothing to
    // decode). Bypass the crop step and leave the raw file on the input so the
    // server's content-type validation produces the canonical inline error
    // (`errors.cover_image`) on submit — preserving the pre-OKS-697 reject
    // contract. Only accepted image types route through the mandatory crop.
    if (!COVER_ACCEPT_TYPES.includes(file.type)) {
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev)
        return URL.createObjectURL(file)
      })
      return
    }

    // Image: route every pick/replace through the crop step. The OS picker
    // populates the hidden input with the UNCROPPED original; clear it
    // immediately so a form submit before *Appliquer* can't send the raw
    // file. Only `onCropApply` re-populates the input (with the 3:2 Blob).
    if (inputRef.current) inputRef.current.value = ""
    setCropMime(file.type || undefined)
    setCropSrc(URL.createObjectURL(file))
    setCropOpen(true)
  }

  // OKS-697 — apply the cropped 3:2 Blob: wrap it in a File, inject it into
  // the hidden multipart input via `DataTransfer` (the only way to
  // programmatically set `input.files`), and update the preview.
  const onCropApply = (blob: Blob) => {
    const ext =
      blob.type === "image/png"
        ? "png"
        : blob.type === "image/webp"
          ? "webp"
          : "jpg"
    const file = new File([blob], `mission-cover.${ext}`, {
      type: blob.type || "image/jpeg",
    })
    if (inputRef.current) {
      const transfer = new DataTransfer()
      transfer.items.add(file)
      inputRef.current.files = transfer.files
    }
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev)
      return URL.createObjectURL(blob)
    })
    closeCropper()
  }

  const onCropCancel = () => {
    // No file staged — the input stays cleared, the prior preview/cover is
    // untouched (UX Spec §1 "Cancel crop"). The below-min advisory (probed on
    // the just-picked source) is dropped since nothing was kept.
    setDimensionWarning(false)
    closeCropper()
  }

  const closeCropper = () => {
    setCropOpen(false)
    if (cropSrc) URL.revokeObjectURL(cropSrc)
    setCropSrc(null)
    setCropMime(undefined)
  }

  const onClear = () => {
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev)
      return null
    })
    setDimensionWarning(false)
    if (inputRef.current) inputRef.current.value = ""
  }

  const onReplaceClick = () => {
    inputRef.current?.click()
  }

  const confirmRemove = () => {
    setRemoveStaged(true)
    onClear()
    setRemoveOpen(false)
  }

  const undoRemove = () => {
    setRemoveStaged(false)
  }

  // Freshly picked file > current cover (unless removal staged) > empty.
  const displayUrl = previewUrl ?? (removeStaged ? null : currentUrl)
  // OKS-452 — true only while `displayUrl` IS the server-provided
  // `currentUrl` (no local pick in front of it, not staged for removal) —
  // the only branch the edit-load skeleton applies to.
  const showingServerCover = !previewUrl && !removeStaged && Boolean(currentUrl)
  // The legacy note only makes sense while the legacy cover is still
  // showing (no new pick, no staged removal).
  const showLegacyNote = isLegacy && !previewUrl && !removeStaged
  // Advisory makes sense while a below-min source is being cropped (cropper
  // open) or after it's been applied as the preview (OKS-697 — the source is
  // probed on pick, before the cropped preview exists).
  const showDimensionWarning =
    dimensionWarning && (Boolean(previewUrl) || cropOpen)
  const describedBy =
    [
      hasError ? errorId : null,
      showDimensionWarning ? warningId : null,
      helperId,
    ]
      .filter(Boolean)
      .join(" ") || undefined

  return (
    <div data-testid={testId} className="grid gap-2">
      <Label htmlFor={inputId}>{t(copy.label)}</Label>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
        {displayUrl ? (
          <>
            {/* OKS-452 — edit-load skeleton: shows only while the
                server-provided `currentUrl` is loading for the first time;
                the `<img>` stays mounted (so `onLoad` still fires) but
                visually hidden underneath until it resolves. */}
            {showingServerCover && !currentUrlLoaded ? (
              <Skeleton
                data-testid={`${testId}-loading-skeleton`}
                className={cn(
                  COVER_ASPECT_CLASS,
                  "w-full max-w-xs shrink-0 rounded-lg",
                )}
              />
            ) : null}
            <img
              src={displayUrl}
              alt={t(copy.previewAlt)}
              data-testid={`${testId}-preview`}
              onLoad={() => {
                if (showingServerCover) setCurrentUrlLoaded(true)
              }}
              className={cn(
                COVER_ASPECT_CLASS,
                "w-full max-w-xs shrink-0 rounded-lg object-cover",
                showingServerCover && !currentUrlLoaded && "hidden",
              )}
            />
          </>
        ) : (
          <div
            aria-hidden="true"
            data-testid={`${testId}-fallback`}
            className={cn(
              COVER_ASPECT_CLASS,
              "bg-muted text-muted-foreground flex w-full max-w-xs shrink-0 items-center justify-center rounded-lg",
            )}
          >
            <ImageIcon className="size-8" />
          </div>
        )}

        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={onReplaceClick}
            data-testid={`${testId}-upload`}
            className="min-h-11"
          >
            {hasCoverImage || previewUrl ? t(copy.replace) : t(copy.upload)}
          </Button>

          {previewUrl ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClear}
              data-testid={`${testId}-clear`}
              className="text-muted-foreground sm:self-center"
            >
              {t(copy.clear)}
            </Button>
          ) : null}

          {hasCoverImage && !previewUrl && !removeStaged ? (
            <Button
              type="button"
              variant="ghost"
              onClick={() => setRemoveOpen(true)}
              data-testid={`${testId}-remove`}
              className="min-h-11 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
            >
              {t(copy.remove)}
            </Button>
          ) : null}

          {removeStaged ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={undoRemove}
              data-testid={`${testId}-remove-undo`}
              className="text-muted-foreground sm:self-center"
            >
              {t(copy.removeUndo)}
            </Button>
          ) : null}
        </div>
      </div>

      {showLegacyNote ? (
        <p
          className="text-muted-foreground text-xs"
          data-testid={`${testId}-legacy-note`}
        >
          {t(copy.legacyNote)}
        </p>
      ) : null}

      {/* Hidden file input lives inside the parent Inertia <Form> so the
          submit is auto-detected as multipart. */}
      <Input
        ref={inputRef}
        id={inputId}
        type="file"
        name={inputName}
        accept={COVER_ACCEPT_TYPES.join(",")}
        onChange={onPick}
        className="sr-only"
        data-testid={`${testId}-input`}
        aria-invalid={hasError}
        aria-describedby={describedBy}
      />

      {/* Staged removal — only posted when the admin confirmed Remove. */}
      {removeStaged ? (
        <input
          type="hidden"
          name={removeName}
          value="1"
          data-testid={`${testId}-remove-flag`}
        />
      ) : null}

      <p id={helperId} className={cn("text-muted-foreground text-xs")}>
        {t(copy.hint)}
      </p>

      {/* OKS-643 — non-blocking below-minimum advisory. Amber (advisory),
          NOT destructive — semantically distinct from the hard `error`
          below (which owns role="alert"); never gates submit. */}
      {showDimensionWarning ? (
        <p
          id={warningId}
          data-testid={`${testId}-dimensions-warning`}
          className="text-sm text-amber-600 dark:text-amber-500"
        >
          {t(copy.dimensionsWarning, {
            width: minDimensions?.[0] ?? MISSION_COVER_MIN_W,
            height: minDimensions?.[1] ?? MISSION_COVER_MIN_H,
          })}
        </p>
      ) : null}

      {hasError ? (
        <p id={errorId} role="alert" className="text-destructive text-sm">
          {Array.isArray(error) ? error[0] : error}
        </p>
      ) : null}

      <Dialog open={removeOpen} onOpenChange={setRemoveOpen}>
        <DialogContent data-testid={`${testId}-remove-dialog`}>
          <DialogTitle>{t(copy.removeConfirmTitle)}</DialogTitle>
          <DialogDescription>{t(copy.removeConfirmBody)}</DialogDescription>
          <DialogFooter>
            <DialogClose
              render={
                <Button
                  variant="secondary"
                  autoFocus
                  data-testid={`${testId}-remove-cancel`}
                >
                  {t(copy.removeConfirmCancel)}
                </Button>
              }
            />
            <Button
              variant="destructive"
              onClick={confirmRemove}
              data-testid={`${testId}-remove-confirm`}
            >
              {t(copy.removeConfirmCta)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* OKS-697 — crop-to-3:2 step for every pick/replace. */}
      <MissionCoverCropper
        open={cropOpen}
        imageSrc={cropSrc}
        mimeType={cropMime}
        onApply={onCropApply}
        onCancel={onCropCancel}
      />
    </div>
  )
}

export default MissionCoverImageField
